import React, { useEffect, useMemo, useRef, useState } from 'react';

type DatePickerFieldProps = {
  id?: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  min?: string;
  max?: string;
  className?: string;
};

const monthLabelFormatter = new Intl.DateTimeFormat('nl-NL', { month: 'long', year: 'numeric' });
const displayDateFormatter = new Intl.DateTimeFormat('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' });
const weekdayLabels = ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'];

const pad = (value: number): string => String(value).padStart(2, '0');

const toIsoDate = (value: Date): string => `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;

const fromIsoDate = (value?: string): Date | null => {
  if (!value) return null;
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day, 12, 0, 0, 0);
};

const startOfMonth = (value: Date): Date => new Date(value.getFullYear(), value.getMonth(), 1, 12, 0, 0, 0);

const shiftMonth = (value: Date, delta: number): Date => new Date(value.getFullYear(), value.getMonth() + delta, 1, 12, 0, 0, 0);

const isSameDay = (left: Date, right: Date): boolean =>
  left.getFullYear() === right.getFullYear()
  && left.getMonth() === right.getMonth()
  && left.getDate() === right.getDate();

const isOutOfRange = (value: Date, min?: Date | null, max?: Date | null): boolean => {
  if (min && value.getTime() < min.getTime()) return true;
  if (max && value.getTime() > max.getTime()) return true;
  return false;
};

const formatDisplayDate = (value: string): string => {
  const parsed = fromIsoDate(value);
  return parsed ? displayDateFormatter.format(parsed) : 'Kies datum';
};

export const DatePickerField: React.FC<DatePickerFieldProps> = ({
  id,
  label,
  value,
  onChange,
  min,
  max,
  className = '',
}) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const selectedDate = useMemo(() => fromIsoDate(value), [value]);
  const minDate = useMemo(() => fromIsoDate(min), [min]);
  const maxDate = useMemo(() => fromIsoDate(max), [max]);
  const [isOpen, setIsOpen] = useState(false);
  const [visibleMonth, setVisibleMonth] = useState(() => startOfMonth(selectedDate || maxDate || new Date()));

  useEffect(() => {
    if (selectedDate) {
      setVisibleMonth(startOfMonth(selectedDate));
    }
  }, [selectedDate]);

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const days = useMemo(() => {
    const firstDay = startOfMonth(visibleMonth);
    const weekdayOffset = (firstDay.getDay() + 6) % 7;
    const gridStart = new Date(firstDay);
    gridStart.setDate(firstDay.getDate() - weekdayOffset);

    return Array.from({ length: 42 }, (_, index) => {
      const day = new Date(gridStart);
      day.setDate(gridStart.getDate() + index);
      return day;
    });
  }, [visibleMonth]);

  const currentMonthLabel = monthLabelFormatter.format(visibleMonth);
  const canGoPrevious = !minDate || shiftMonth(visibleMonth, -1).getTime() >= startOfMonth(minDate).getTime();
  const canGoNext = !maxDate || shiftMonth(visibleMonth, 1).getTime() <= startOfMonth(maxDate).getTime();

  const handleSelect = (date: Date) => {
    if (isOutOfRange(date, minDate, maxDate)) return;
    onChange(toIsoDate(date));
    setIsOpen(false);
  };

  return (
    <div className={`tracker-date-picker-field ${className}`.trim()} ref={wrapperRef}>
      <label htmlFor={id}>{label}</label>
      <button
        id={id}
        type="button"
        className="tracker-date-picker-trigger"
        onClick={() => setIsOpen((current) => !current)}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      >
        <span>{formatDisplayDate(value)}</span>
        <span className="tracker-date-picker-icon" aria-hidden="true" />
      </button>

      {isOpen && (
        <div className="tracker-date-picker-popover" role="dialog" aria-label={`${label} kiezen`}>
          <div className="tracker-date-picker-header">
            <button type="button" onClick={() => setVisibleMonth((current) => shiftMonth(current, -1))} disabled={!canGoPrevious} aria-label="Vorige maand">‹</button>
            <strong>{currentMonthLabel.charAt(0).toUpperCase() + currentMonthLabel.slice(1)}</strong>
            <button type="button" onClick={() => setVisibleMonth((current) => shiftMonth(current, 1))} disabled={!canGoNext} aria-label="Volgende maand">›</button>
          </div>

          <div className="tracker-date-picker-weekdays" aria-hidden="true">
            {weekdayLabels.map((weekday) => <span key={weekday}>{weekday}</span>)}
          </div>

          <div className="tracker-date-picker-grid">
            {days.map((day) => {
              const inMonth = day.getMonth() === visibleMonth.getMonth();
              const selected = selectedDate ? isSameDay(day, selectedDate) : false;
              const disabled = isOutOfRange(day, minDate, maxDate);
              return (
                <button
                  key={toIsoDate(day)}
                  type="button"
                  className={`tracker-date-picker-day ${inMonth ? '' : 'is-muted'} ${selected ? 'is-selected' : ''}`}
                  onClick={() => handleSelect(day)}
                  disabled={disabled}
                >
                  {day.getDate()}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default DatePickerField;